import { useCallback, useEffect, useRef, useState } from 'react'
import { returnFocusAfterDialog } from 'src/utils/dialogFocus'

/** Backstop if the drawer's slide-out transition never reports it ended. */
export const DRAWER_CLOSE_BACKSTOP_MS = 600

/**
 * antd 3 Drawer keeps its closed panel in the page. This keeps it mounted only
 * while open or sliding out, then removes it and returns focus to whatever
 * opened it (or `fallbackFocus` when that control is gone).
 *
 * Render: `{drawer.mounted ? <Drawer visible={drawer.open}
 *   onClose={drawer.hide} afterVisibleChange={drawer.afterVisibleChange} /> : null}`
 */
const useFocusReturningDrawer = ({ fallbackFocus = null } = {}) => {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const openRef = useRef(false)
  const openerRef = useRef(null)
  const backstopRef = useRef(null)
  const fallbackRef = useRef(fallbackFocus)
  fallbackRef.current = fallbackFocus

  const clearBackstop = () => {
    if (backstopRef.current) clearTimeout(backstopRef.current)
    backstopRef.current = null
  }

  const finishClose = useCallback(() => {
    clearBackstop()
    if (openRef.current) return
    setMounted(false)
    const opener = openerRef.current
    openerRef.current = null
    returnFocusAfterDialog(opener, fallbackRef.current)
  }, [])

  const show = useCallback(() => {
    clearBackstop()
    if (!openRef.current) openerRef.current = document.activeElement
    openRef.current = true
    setMounted(true)
    setOpen(true)
  }, [])

  const hide = useCallback(() => {
    if (!openRef.current) return
    openRef.current = false
    setOpen(false)
    clearBackstop()
    backstopRef.current = setTimeout(finishClose, DRAWER_CLOSE_BACKSTOP_MS)
  }, [finishClose])

  const afterVisibleChange = useCallback(
    visible => {
      if (!visible) finishClose()
    },
    [finishClose],
  )

  useEffect(() => clearBackstop, [])

  return { open, mounted, show, hide, afterVisibleChange }
}

export default useFocusReturningDrawer
